import { useCallback, useContext, useEffect, useState } from "react";
import { GlobalIcon, IconNameT } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../api/request/Lead/Lead.service";
import { DataI } from "../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { PendingCapitalI } from "../../../../../api/request/Lead/Interfaces/PolicyInterface";
import { ACCENT, ACCENT_SOFT } from "../components/modifications/shared";
import AddressChangeFlow from "../components/modifications/AddressChangeFlow";
import NameChangeFlow from "../components/modifications/NameChangeFlow";
import NieToDniFlow from "../components/modifications/NieToDniFlow";
import EmailChangeFlow from "../components/modifications/EmailChangeFlow";
import PhoneChangeFlow from "../components/modifications/PhoneChangeFlow";
import BeneficiariesFlow from "../components/modifications/BeneficiariesFlow";
import CapitalReductionFlow from "../components/modifications/CapitalReductionFlow";

interface ModificationsPageProps {
  userData?: DataI;
  insuredCapital: number;
  // Bajada aprobada que aún no rige (gf-monorepo #10199).
  pendingCapital?: PendingCapitalI | null;
}

type FlowT =
  | "address"
  | "email"
  | "phone"
  | "beneficiaries"
  | "nieToDni"
  | "name"
  | "capital"
  | null;

interface CapitalContext {
  eligible: boolean;
  currentCapital: number | null;
  minCapital: number;
  capitalStep?: number;
  minTraitCapital?: number;
  capitalsByTrait: Record<string, number> | null;
  renewalDate: string | null;
  effectiveDate: string | null;
}

interface HistoryItem {
  id: string;
  type: string;
  typeLabel: string;
  status: string;
  createdAt: string;
  signedAt: string | null;
  completedAt: string | null;
}

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: "#f3f4f6", color: "#4b5563" },
  SIGNATURE_PENDING: { bg: "#fef3c7", color: "#92400e" },
  SIGNED: { bg: "#dbeafe", color: "#1d4ed8" },
  COMPLETED: { bg: "#dcfce7", color: "#15803d" },
  REJECTED: { bg: "#fee2e2", color: "#b91c1c" },
};

const ModificationsPage = ({ userData, insuredCapital, pendingCapital }: ModificationsPageProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token } = useContext(GetlifeContext);

  const [activeFlow, setActiveFlow] = useState<FlowT>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [capitalContext, setCapitalContext] = useState<CapitalContext | null>(null);

  const t = (key: string) => translate(`newDashboard.modifications.${key}`);

  const loadHistory = useCallback(async () => {
    try {
      const repository = new HttpLeadRepository(leadId, token);
      const response = await repository.getModificationRequests();
      setHistory(response.items ?? []);
    } catch (error) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [leadId, token]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Capital reduction is version-gated (AXA >V6 / AXA_WL desde V1): the card
  // only shows when the backend says the policy is eligible.
  useEffect(() => {
    let cancelled = false;
    const loadCapitalContext = async () => {
      try {
        const repository = new HttpLeadRepository(leadId, token);
        const context = await repository.getCapitalReductionContext();
        if (!cancelled) setCapitalContext(context);
      } catch (error) {
        if (!cancelled) setCapitalContext(null);
      }
    };
    loadCapitalContext();
    return () => {
      cancelled = true;
    };
  }, [leadId, token]);

  const options: { key: Exclude<FlowT, null>; icon: IconNameT }[] = [
    { key: "address", icon: "Home2Icon" },
    { key: "email", icon: "MailIcon" },
    { key: "phone", icon: "PhoneIcon" },
    { key: "beneficiaries", icon: "SimpleUserIcon" },
    { key: "nieToDni", icon: "FileInvoiceIcon" },
    { key: "name", icon: "PencilIcon" },
    ...(capitalContext?.eligible
      ? ([{ key: "capital", icon: "ShieldCheckIcon" }] as {
          key: Exclude<FlowT, null>;
          icon: IconNameT;
        }[])
      : []),
  ];

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    padding: 24,
  };

  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1
    ).padStart(2, "0")}/${date.getFullYear()}`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: "0 0 4px" }}>
          {t("pageTitle")}
        </h1>
        <p style={{ fontSize: 15, color: "#6b7280", margin: 0 }}>{t("pageDesc")}</p>
      </div>

      {/* Options grid */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 16px" }}>
          {t("chooseTitle")}
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 12,
          }}
        >
          {options.map((option) => (
            <button
              key={option.key}
              onClick={() => setActiveFlow(option.key)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                textAlign: "left",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                backgroundColor: "#fff",
                padding: "16px",
                cursor: "pointer",
                transition: "border-color 0.15s, background-color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = ACCENT;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: ACCENT_SOFT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <GlobalIcon iconName={option.icon} size="XS" color={ACCENT} />
              </div>
              <div>
                <span style={{ display: "block", fontSize: 15, fontWeight: 600, color: "#111827" }}>
                  {t(`option.${option.key}.title`)}
                </span>
                <span style={{ display: "block", fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                  {t(`option.${option.key}.desc`)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* History */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 16px" }}>
          {t("historyTitle")}
        </h2>
        {historyLoading ? (
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>…</p>
        ) : history.length === 0 ? (
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>{t("historyEmpty")}</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {history.map((item, index) => {
              const badge = STATUS_STYLES[item.status] ?? STATUS_STYLES.DRAFT;
              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "12px 0",
                    borderBottom:
                      index === history.length - 1 ? "none" : "1px solid #f3f4f6",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: "#111827", margin: 0 }}>
                      {item.typeLabel}
                    </p>
                    <p style={{ fontSize: 13, color: "#6b7280", margin: "2px 0 0" }}>
                      {formatDate(item.createdAt)}
                    </p>
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      padding: "4px 10px",
                      borderRadius: 999,
                      backgroundColor: badge.bg,
                      color: badge.color,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t(`status.${item.status}`)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Flows */}
      {activeFlow === "address" && (
        <AddressChangeFlow onClose={() => setActiveFlow(null)} onSubmitted={loadHistory} />
      )}
      {activeFlow === "name" && (
        <NameChangeFlow onClose={() => setActiveFlow(null)} onSubmitted={loadHistory} />
      )}
      {activeFlow === "nieToDni" && (
        <NieToDniFlow onClose={() => setActiveFlow(null)} onSubmitted={loadHistory} />
      )}
      {activeFlow === "email" && (
        <EmailChangeFlow
          currentEmail={userData?.lead?.email ?? ""}
          onClose={() => setActiveFlow(null)}
          onSubmitted={loadHistory}
        />
      )}
      {activeFlow === "phone" && (
        <PhoneChangeFlow
          currentPhone={userData?.lead?.phone ?? ""}
          onClose={() => setActiveFlow(null)}
          onSubmitted={loadHistory}
        />
      )}
      {activeFlow === "capital" && capitalContext?.eligible && (
        <CapitalReductionFlow
          currentCapital={capitalContext.currentCapital ?? 0}
          minCapital={capitalContext.minCapital}
          capitalStep={capitalContext.capitalStep}
          minTraitCapital={capitalContext.minTraitCapital}
          capitalsByTrait={capitalContext.capitalsByTrait ?? null}
          pendingCapital={pendingCapital}
          renewalDate={capitalContext.renewalDate}
          effectiveDate={capitalContext.effectiveDate}
          onClose={() => setActiveFlow(null)}
          onSubmitted={loadHistory}
        />
      )}
      {activeFlow === "beneficiaries" && (
        <BeneficiariesFlow
          insuredCapital={insuredCapital}
          pendingCapital={pendingCapital}
          onClose={() => setActiveFlow(null)}
          onSubmitted={loadHistory}
        />
      )}
    </div>
  );
};

export default ModificationsPage;
